import type { DocumentData } from 'firebase/firestore'
import { COST_TYPES, USER_PLANS } from '@/lib/constants'
import type {
  CostType,
  FeedbackMessage,
  Material,
  MaterialAttributeValue,
  MaterialCategory,
  MaterialCategoryAttribute,
  MaterialPrice,
  Translations,
  UnitOfMeasure,
  UserPlan,
  UserProfile,
  UserRole,
} from '@/lib/types'
import { toDate } from '@/lib/utils'

/**
 * One place that turns a `users/{uid}` document into a typed profile. Unknown values degrade to the
 * safe option (`user`, no plan) instead of throwing: the console must stay usable even if a document
 * was written by an older version of the app.
 */
export function mapUserProfile(id: string, data: DocumentData | undefined): UserProfile {
  return {
    uid: id,
    email: asString(data?.email),
    displayName: asString(data?.displayName),
    role: parseRole(data?.role),
    plan: parsePlan(data?.plan),
    // Only an explicit `false` deactivates an account; a missing field is an active one.
    isActive: data?.isActive !== false,
    createdAt: data?.createdAt,
  }
}

export function parseRole(value: unknown): UserRole {
  return typeof value === 'string' && value.toLowerCase() === 'admin' ? 'admin' : 'user'
}

export function parsePlan(value: unknown): UserPlan | null {
  if (typeof value !== 'string') return null
  const normalized = value.toLowerCase() as UserPlan
  return USER_PLANS.includes(normalized) ? normalized : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

/** `feedback/{id}` → typed message. Timestamps become epoch millis, or null when not written yet. */
export function mapFeedback(id: string, data: DocumentData | undefined): FeedbackMessage {
  return {
    id,
    type: data?.type === 'suggestion' ? 'suggestion' : 'problem',
    status: data?.status === 'reviewed' ? 'reviewed' : 'new',
    text: typeof data?.text === 'string' ? data.text : '',
    userId: asString(data?.userId) ?? '',
    userName: asString(data?.userName) ?? '',
    createdAt: toMillis(data?.createdAt),
    reviewedBy: asString(data?.reviewedBy),
    reviewedAt: toMillis(data?.reviewedAt),
  }
}

function toMillis(value: unknown): number | null {
  return toDate(value)?.getTime() ?? null
}

/** A `nameI18n`/`descriptionI18n` map, keeping only non-blank string values. */
export function mapTranslations(value: unknown): Translations {
  if (typeof value !== 'object' || value === null) return {}
  const result: Translations = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string' && item.trim().length > 0) result[key] = item
  }
  return result
}

function parseCostType(value: unknown): CostType {
  return typeof value === 'string' && COST_TYPES.includes(value as CostType)
    ? (value as CostType)
    : 'MATERIAL'
}

/** `materials/{id}` → typed material. A document without a name is not usable and is dropped. */
export function mapMaterial(id: string, data: DocumentData | undefined): Material | null {
  const name = asString(data?.name)
  if (name === null) return null
  return {
    id,
    name,
    nameI18n: mapTranslations(data?.nameI18n),
    code: asString(data?.code) ?? '',
    category: asString(data?.category) ?? '',
    categoryId: asString(data?.categoryId) ?? '',
    unit: asString(data?.unit) ?? '',
    unitId: asString(data?.unitId) ?? '',
    description: asString(data?.description) ?? '',
    descriptionI18n: mapTranslations(data?.descriptionI18n),
    costType: parseCostType(data?.costType),
    isActive: data?.isActive !== false,
    // Missing means private: never treat an unknown document as published.
    isPrivate: data?.isPrivate !== false,
    ownerId: asString(data?.ownerId) ?? '',
    createdByName: asString(data?.createdByName) ?? '',
    updatedByName: asString(data?.updatedByName) ?? '',
    createdAt: toMillis(data?.createdAt),
    updatedAt: toMillis(data?.updatedAt),
  }
}

export function mapCategory(id: string, data: DocumentData | undefined): MaterialCategory | null {
  const name = asString(data?.name)
  if (name === null) return null
  return {
    id,
    name,
    nameI18n: mapTranslations(data?.nameI18n),
    // `code` (an unused free-text field of older documents) is no longer read.
    orderIndex: typeof data?.orderIndex === 'number' ? data.orderIndex : 0,
    costType: parseCostType(data?.costType),
    groupName: asString(data?.groupName) ?? '',
  }
}

export function mapUnit(id: string, data: DocumentData | undefined): UnitOfMeasure | null {
  const name = asString(data?.name)
  if (name === null) return null
  return {
    id,
    name,
    nameI18n: mapTranslations(data?.nameI18n),
    symbol: asString(data?.symbol) ?? '',
    isActive: data?.isActive !== false,
  }
}

/** `materialCategoriesAttrs/{id}` → typed attribute of a category. */export function mapAttribute(
  id: string,
  data: DocumentData | undefined,
): MaterialCategoryAttribute | null {
  const name = asString(data?.name)
  if (name === null) return null
  return {
    id,
    categoryId: asString(data?.categoryId) ?? '',
    name,
    nameI18n: mapTranslations(data?.nameI18n),
    code: asString(data?.code) ?? '',
    type: data?.type === 'STRING' ? 'STRING' : 'NUMBER',
    orderIndex: typeof data?.orderIndex === 'number' ? data.orderIndex : 0,
  }
}

/** `materialAttrsValues/{id}` → one attribute value of one material. */export function mapAttributeValue(
  id: string,
  data: DocumentData | undefined,
): MaterialAttributeValue | null {
  const materialId = asString(data?.materialId)
  const attributeId = asString(data?.attributeId)
  if (materialId === null || attributeId === null) return null
  return {
    id,
    materialId,
    categoryId: asString(data?.categoryId) ?? '',
    categoryName: asString(data?.categoryName) ?? '',
    attributeId,
    attributeCode: asString(data?.attributeCode) ?? '',
    attributeName: asString(data?.attributeName) ?? '',
    value: typeof data?.value === 'string' ? data.value : '',
    type: data?.type === 'STRING' ? 'STRING' : 'NUMBER',
  }
}

/** `prices/{materialId}` → typed price. A document without a numeric amount is unusable. */
export function mapPrice(id: string, data: DocumentData | undefined): MaterialPrice | null {
  if (typeof data?.amount !== 'number') return null
  return {
    materialId: asString(data?.materialId) ?? id,
    amount: data.amount,
    currency: data?.currency === 'USD' ? 'USD' : 'AZN',
    ownerId: asString(data?.ownerId) ?? '',
    isPrivate: data?.isPrivate !== false,
    updatedByName: asString(data?.updatedByName) ?? '',
    updatedAt: toMillis(data?.updatedAt),
  }
}
